import React, { useState } from "react";
import { Alert, Button, Empty, Image, Typography, Upload, message } from "antd";
import { DeleteOutlined, DownOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { BannerUploadEntry, removeDesktopBanner } from "./bannerThemeUtils";

const accept = "image/jpeg,image/png,image/webp";
const validImage = (file: File) => {
  if (["image/jpeg", "image/png", "image/webp"].includes(file.type)) return true;
  message.error("Choisissez une image JPG, PNG ou WebP.");
  return false;
};

interface Props {
  desktop: BannerUploadEntry[]; mobile: BannerUploadEntry[]; disabled: boolean;
  onChange: (desktop: BannerUploadEntry[], mobile: BannerUploadEntry[]) => void;
  onPreview: (index: number) => void;
}

export default function StorefrontBanners({ desktop, mobile, disabled, onChange, onPreview }: Props) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? desktop : desktop.slice(0, 3);
  return <section aria-labelledby="storefront-banners-title">
    <div className="storefront-section-heading">
      <div><h2 id="storefront-banners-title">Bannières & contenu</h2>
        <p>Associez chaque visuel desktop à sa version mobile.</p></div>
      <div className="storefront-banner-toolbar"><Typography.Text type="secondary">{desktop.length} visuels sur 10</Typography.Text>
        <Upload multiple accept={accept} aria-label="Fichiers des bannières desktop" showUploadList={false} disabled={disabled || desktop.length >= 10}
          beforeUpload={(file, files) => {
            if (file.uid === files[0].uid) {
              const available = 10 - desktop.length;
              const accepted = files.filter(validImage);
              if (accepted.length > available) message.warning("Vous pouvez ajouter jusqu'à 10 bannières desktop.");
              const added = accepted.slice(0, available).map((entry, index) => ({
                uid: entry.uid, name: entry.name, originFileObj: entry, order: desktop.length + index,
              }));
              if (added.length) onChange([...desktop, ...added], mobile);
            }
            return Upload.LIST_IGNORE;
          }}>
          <Button icon={<PlusOutlined />} disabled={disabled || desktop.length >= 10}>Ajouter des bannières</Button>
        </Upload>
      </div>
    </div>
    {desktop.length > 0 && mobile.length < desktop.length && <Alert type="warning" showIcon
      message={`Versions mobiles facultatives : ${mobile.length} sur ${desktop.length}`}
      description="Sans version mobile, le visuel desktop reste utilisé." />}
    {desktop.length >= 10 && <p className="storefront-help">Limite de 10 bannières atteinte. Retirez un visuel pour en ajouter un autre.</p>}
    {!desktop.length ? <Empty description="Ajoutez une première bannière desktop pour personnaliser la vitrine." /> : <div className="storefront-banner-list">
      <div className="storefront-banner-columns" aria-hidden="true"><span>Ordinateur · 1920 × 800</span><span>Mobile · 1080 × 1350</span></div>
      {visible.map((banner, index) => {
        const mobileBanner = mobile.find((entry) => entry.order === index);
        const addMobile = <Upload accept={accept} aria-label={`Fichier mobile de la bannière ${index + 1}`} showUploadList={false} disabled={disabled} beforeUpload={(file) => {
          if (validImage(file)) onChange(desktop, [...mobile.filter((entry) => entry.order !== index), {
            uid: file.uid, name: file.name, originFileObj: file, order: index,
          }]);
          return Upload.LIST_IGNORE;
        }}>
          <Button className={mobileBanner ? "storefront-mobile-replace" : "storefront-mobile-upload"} disabled={disabled}
            icon={<UploadOutlined />} aria-label={`${mobileBanner ? "Remplacer" : "Ajouter"} la version mobile de la bannière ${index + 1}`}>
            {mobileBanner ? "Remplacer" : <span>Ajouter une version<small>1080 × 1350</small></span>}
          </Button>
        </Upload>;
        return <div className="storefront-banner-row" key={banner.uid || index}>
          <div className="storefront-desktop-banner">
            <div className="storefront-banner-label"><button type="button" onClick={() => onPreview(index)}
              aria-label={`Prévisualiser la bannière ${index + 1}`}>Bannière {String(index + 1).padStart(2, "0")}</button>
              <span className="storefront-mobile-label">Ordinateur · 1920 × 800</span></div>
            <div className="storefront-banner-image-row"><Image src={banner.thumbUrl || banner.url} alt={`Bannière desktop ${index + 1}`} />
              <Button danger icon={<DeleteOutlined />} disabled={disabled} aria-label={`Supprimer la bannière ${index + 1}`} onClick={() => {
                const next = removeDesktopBanner(desktop, mobile, index);
                onChange(next.desktop, next.mobile);
              }} /></div>
          </div>
          <div className="storefront-mobile-banner"><span className="storefront-mobile-label">Mobile · facultatif</span>
            {mobileBanner ? <><Image src={mobileBanner.thumbUrl || mobileBanner.url} alt={`Version mobile de la bannière ${index + 1}`} />
              <div className="storefront-mobile-actions">{addMobile}<Button danger type="text" size="small" disabled={disabled}
                icon={<DeleteOutlined />} aria-label={`Retirer la version mobile de la bannière ${index + 1}`}
                onClick={() => onChange(desktop, mobile.filter((entry) => entry.order !== index))} /></div></> : addMobile}
          </div>
        </div>;
      })}
      {desktop.length > 3 && <Button type="text" className="storefront-banner-more" icon={<DownOutlined rotate={expanded ? 180 : 0} />}
        aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        {expanded ? "Afficher les 3 premières bannières" : `Voir les ${desktop.length - 3} autres bannières`}
      </Button>}
    </div>}
    <p className="storefront-help">JPG, PNG ou WebP. Desktop : 1920 × 800 px. Mobile : 1080 × 1350 px (ratio 4:5).</p>
  </section>;
}
